import { useState } from "react";
import clsx from "clsx";

import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import { Select } from "../base/Select";
import { TextField } from "../base/TextField";
import SortableList from "./SortableList";
import AspectPickerModal from "./AspectPickerModal";
import {
  ITitleAspectPriority,
  TITLE_FIELD_WEIGHTS,
  TitleFieldWeight,
} from "../../models/Category/TitleConfig";

interface AspectPriorityListProps {
  partCategoryId: string;
  aspects: ITitleAspectPriority[];
  /** The Aspects field's own weight, for the warning when aspects can never reach the title. */
  aspectsFieldWeight: TitleFieldWeight | undefined;
  onChange: (aspects: ITitleAspectPriority[]) => void;
}

const WEIGHT_STYLES: Record<TitleFieldWeight, string> = {
  Mandatory: "bg-green-100 text-green-800",
  High: "bg-emerald-50 text-emerald-700",
  Medium: "bg-amber-50 text-amber-700",
  Low: "bg-gray-100 text-gray-600",
  Excluded: "bg-red-50 text-red-700",
};

/**
 * Per-aspect priority, the aspect-level twin of FieldPriorityList. Aspects are eBay item
 * specifics rather than a fixed enum, so they are picked from the category's eBay aspects or
 * typed by hand, and each carries its own weight - Mandatory on "Mirror Adjustment Method" keeps
 * Powerfold or Manual in the title even while other aspects are being trimmed away.
 */
export default function AspectPriorityList({
  partCategoryId,
  aspects,
  aspectsFieldWeight,
  onChange,
}: AspectPriorityListProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customName, setCustomName] = useState("");

  const names = aspects.map((a) => a.name);
  const lowered = new Set(names.map((n) => n.toLowerCase()));

  const mandatoryCount = aspects.filter(
    (a) => a.weight === "Mandatory",
  ).length;

  // A mandatory aspect cannot reach the title if the Aspects field itself is excluded, and that
  // is invisible from this panel alone, so it is called out here rather than left to be puzzled
  // over in the preview.
  const aspectsFieldExcluded = aspectsFieldWeight === "Excluded";
  const aspectsFieldMissing = aspectsFieldWeight === undefined;

  const setWeight = (name: string, weight: TitleFieldWeight) =>
    onChange(aspects.map((a) => (a.name === name ? { ...a, weight } : a)));

  const removeAspect = (name: string) =>
    onChange(aspects.filter((a) => a.name !== name));

  const addNames = (toAdd: string[]) => {
    const additions = toAdd
      .map((name) => name.trim())
      .filter((name) => name && !lowered.has(name.toLowerCase()))
      .map<ITitleAspectPriority>((name) => ({ name, weight: "Medium" }));

    if (additions.length > 0) onChange([...aspects, ...additions]);
  };

  const addCustom = () => {
    const name = customName.trim();
    if (!name || lowered.has(name.toLowerCase())) return;

    addNames([name]);
    setCustomName("");
  };

  const handlePicked = (picked: string[]) => {
    addNames(picked);
    setPickerOpen(false);
  };

  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          Aspect Priority
        </h2>
        <Button
          variant="outlined"
          size="xs"
          onClick={() => setPickerOpen(true)}
        >
          Browse eBay aspects
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Which item specifics matter most, and which must always appear. The
        title carries the aspect's value, not its name — Mirror Adjustment
        Method reads as "Powerfold".
      </p>

      {mandatoryCount > 0 && (aspectsFieldExcluded || aspectsFieldMissing) && (
        <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {aspectsFieldExcluded
            ? "Aspects is set to Excluded in the title building priority, so no aspect can reach the title — including the mandatory ones below."
            : "Aspects is not in the title building priority, so no aspect can reach the title — including the mandatory ones below."}
        </p>
      )}

      <SortableList
        items={aspects}
        getKey={(item) => item.name}
        onReorder={onChange}
        emptyText="No aspects configured. Aspects are used in the order they arrive."
        renderItem={(item) => (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm truncate" title={item.name}>
              {item.name}
            </span>

            <div className="flex shrink-0 items-center gap-1">
              <span
                className={clsx(
                  "rounded px-1.5 py-0.5 text-[11px]",
                  WEIGHT_STYLES[item.weight],
                )}
              >
                {item.weight}
              </span>

              <Select
                small
                aria-label={`Weight for ${item.name}`}
                value={item.weight}
                onChange={(e) =>
                  setWeight(item.name, e.currentTarget.value as TitleFieldWeight)
                }
                className="!text-xs !pr-6"
              >
                {TITLE_FIELD_WEIGHTS.map((weight) => (
                  <option key={weight} value={weight}>
                    {weight}
                  </option>
                ))}
              </Select>

              <button
                type="button"
                aria-label={`Remove ${item.name}`}
                onClick={() => removeAspect(item.name)}
                className="text-gray-400 hover:text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      />

      <div className="mt-3 flex items-end gap-2">
        <div className="flex-1">
          <TextField
            small
            fullWidth
            label="Add an aspect by name"
            placeholder="Mirror Adjustment Method"
            value={customName}
            onChange={(e) => setCustomName(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;

              // Otherwise Enter submits whatever form wraps the panel and loses the entry.
              e.preventDefault();
              addCustom();
            }}
          />
        </div>
        <Button
          size="sm"
          variant="outlined"
          onClick={addCustom}
          disabled={
            !customName.trim() || lowered.has(customName.trim().toLowerCase())
          }
        >
          <span className="flex items-center gap-1">
            <PlusIcon className="h-4 w-4" /> Add
          </span>
        </Button>
      </div>

      {!!customName.trim() && lowered.has(customName.trim().toLowerCase()) && (
        <p className="mt-1 text-xs text-amber-700">
          That aspect is already on the list.
        </p>
      )}

      <AspectPickerModal
        open={pickerOpen}
        setOpen={setPickerOpen}
        partCategoryId={partCategoryId}
        existingNames={names}
        onConfirm={handlePicked}
      />
    </section>
  );
}
